import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import React, { useCallback, useEffect, useState } from 'react';
import {
  CellProps,
  ColumnWithStrictAccessor,
  usePagination,
  useRowSelect,
  useTable,
} from 'react-table';
import AdminTableToolbar from './AdminTableToolbar';
import Checkbox from '@material-ui/core/Checkbox';
import TableBody from '@material-ui/core/TableBody';
import TablePaginationFooter from 'components/TablePaginationFooter';
import { AdminBox, useAdminBoxes } from './useAdminBoxes';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableRowSkeleton from 'components/TableRowSkeleton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppPath } from 'AppRoutes';
import { AdminDialogType } from './Admin';

interface Props {
  openDialog: (type: AdminDialogType) => void;
  closeDialog: () => void;
}

const BOX_LINK_TYPE = process.env.REACT_APP_BOX_LINK_TYPE as
  | 'EMPLOYEE'
  | 'PLATE_NUMBER';
export const isPlateNumberLink = BOX_LINK_TYPE === 'PLATE_NUMBER';

const columns: ColumnWithStrictAccessor<AdminBox>[] = [
  { Header: '名前', accessor: 'name' },
  { Header: 'IMEI', accessor: 'imei' },
  { Header: '住所', accessor: 'address' },
  {
    Header: '郵便番号',
    accessor: 'postcode',
    Cell: ({ value }) =>
      value ? `${value.slice(0, 3)}-${value.slice(3)}` : '',
  },
  { Header: '県', accessor: 'prefectureName' },
];

if (isPlateNumberLink) {
  columns.splice(2, 0, { Header: '車番', accessor: 'plateNumber' });
}

const useStyles = makeStyles((theme: Theme) => ({
  selectCol: { width: '1%' },
  row: {
    '&:hover': { backgroundColor: theme.palette.grey[200], cursor: 'pointer' },
  },
}));

export const useNavigateAdminTable = () => {
  const navigate = useNavigate();

  const navigateToAdmin = useCallback(
    () =>
      navigate(
        `${AppPath.Admin}?rows=${initAdminTableParams.rows}&page=${initAdminTableParams.page}`
      ),
    [navigate]
  );

  return navigateToAdmin;
};

const initAdminTableParams = { page: '1', rows: '10' };

const AdminTable = ({ openDialog }: Props) => {
  const classes = useStyles();

  const [params, setParams] = useSearchParams(initAdminTableParams);
  const limit = parseInt(params.get('rows') ?? '10');
  const offset = (parseInt(params.get('page') ?? '1') - 1) * limit;
  const { resolvedData, isLoading } = useAdminBoxes({
    limit,
    offset,
  });
  const [isSkipResetSelect, setIsSkipResetSelect] = useState(false);

  const count = resolvedData?.count ?? 0;

  const pageCount = Math.max(Math.ceil(count / limit), 1);

  const tableInstance = useTable<AdminBox>(
    {
      columns,
      data: resolvedData?.boxes ?? [],
      pageCount,
      manualPagination: true,
      initialState: {
        pageIndex: offset / limit,
        pageSize: limit,
      },
      autoResetSelectedRows: !isSkipResetSelect,
    },
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.allColumns.push((columns) => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <Checkbox
              {...getToggleAllRowsSelectedProps()}
              title="全部を選択する"
            />
          ),
          Cell: ({ row }: CellProps<AdminBox>) => (
            <Checkbox
              {...row.getToggleRowSelectedProps()}
              title="行を選択する"
            />
          ),
        },
        ...columns,
      ]);
    }
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    gotoPage,
    setPageSize,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { pageIndex, pageSize },
  } = tableInstance;

  useEffect(() => {
    setIsSkipResetSelect(selectedFlatRows.length > 0);
  }, [selectedFlatRows]);

  useEffect(() => {
    const rows = pageSize.toString();
    const page = (pageIndex + 1).toString();

    if (params.get('rows') !== rows || params.get('page') !== page) {
      setParams({ rows, page });
    }
  }, [setParams, pageIndex, pageSize, params]);

  const onRowClick = (box: AdminBox) => {
    toggleAllRowsSelected(false);
    openDialog(box);
  };

  return (
    <TableContainer>
      <AdminTableToolbar
        selectedFlatRows={selectedFlatRows}
        toggleAllRowsSelected={toggleAllRowsSelected}
        gotoPage={gotoPage}
        openDialog={openDialog}
      />
      <Table size="small" {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <TableCell
                  className={column.id === 'selection' ? classes.selectCol : ''}
                  {...column.getHeaderProps()}
                >
                  {column.render('Header')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {isLoading ? (
            <TableRowSkeleton
              pageSize={pageSize}
              columns={[{ accessor: '' }, ...columns]}
            />
          ) : (
            page.map((row) => {
              prepareRow(row);
              return (
                <TableRow {...row.getRowProps()} className={classes.row}>
                  {row.cells.map((cell) => {
                    return (
                      <TableCell
                        onClick={
                          cell.column.id !== 'selection'
                            ? () => onRowClick(row.original)
                            : undefined
                        }
                        className={
                          cell.column.id === 'selection'
                            ? classes.selectCol
                            : ''
                        }
                        {...cell.getCellProps()}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          )}
        </TableBody>
        <TablePaginationFooter
          pageCount={pageCount}
          count={count}
          pageIndex={pageIndex}
          pageSize={pageSize}
          gotoPage={gotoPage}
          setPageSize={setPageSize}
          colSpan={columns.length + 1}
        />
      </Table>
    </TableContainer>
  );
};

export default AdminTable;
